import { Component, inject, OnInit, OnDestroy, signal, Injector, runInInjectionContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectUserWithAvatarUrl } from 'src/app/store/auth/auth.selectors';
import { filter, Subscription } from 'rxjs';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { ChallengeService } from 'src/app/services/challenge.service';
import { ADMIN_UID } from 'src/app/config/admin.config';

// ⬇️ lee lastCompletedInterval desde userProgress
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

@Component({
  selector: 'app-tabs',
  templateUrl: './tabs.page.html',
  styleUrls: ['./tabs.page.scss'],
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule, RouterModule],
})
export class TabsPage implements OnInit, OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  private auth = inject(Auth);
  private injector = inject(Injector);
  private challengeService = inject(ChallengeService);
  private firestore = inject(Firestore);

  user$ = this.store.select(selectUserWithAvatarUrl);

  hideTabbar = signal(false);

  // 🔒 Explore bloqueado si aún no hay ningún reto completado
  exploreDisabled = signal(true);

  // 🔒 Global SOLO visible para admin (por UID)
  isAdmin = signal(false);

  private navSub?: Subscription;
  private subs: Subscription[] = [];
  private refreshing = false;

  // 🧠 Último reto completado por el usuario
  private lastCompletedInterval: number | null = null;

  ngOnInit(): void {
    // 🔁 Re-evaluar visibilidad de la tabbar en cada navegación
    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        const url = e.urlAfterRedirects || e.url;

        // Oculta SOLO en "drop view" (explore a pantalla completa o full=1)
        const isDropViewParam = /^\/tabs\/explore\/\d+(?:\?.*)?$/.test(url);
        const hasFullQP =
          /^\/tabs\/explore(?:\/\d+)?\?.*([?&])full=1(&|$)/.test(url);

        const shouldHide = isDropViewParam || hasFullQP;
        this.hideTabbar.set(shouldHide);
        this.applyTabbarVisibility(shouldHide);

        // Re-evaluar el bloqueo de Explore al navegar
        this.refreshExploreLock();
      });

    // aplica al primer render también
    const initialUrl = this.router.url;
    const initialHide =
      /^\/tabs\/explore\/\d+(?:\?.*)?$/.test(initialUrl) ||
      /^\/tabs\/explore(?:\/\d+)?\?.*([?&])full=1(&|$)/.test(initialUrl);
    this.hideTabbar.set(initialHide);
    this.applyTabbarVisibility(initialHide);

    // 🔒 Determinar si el usuario es admin (UID)
    this.subs.push(
      this.user$.subscribe(user => {
        this.isAdmin.set(
          user?.uid === ADMIN_UID
        );
      })
    );

    // ✅ Re-evaluar al cambiar el usuario (login/logout/reset)
    onAuthStateChanged(this.auth, () => {
      this.refreshExploreLock();
    });

    // ✅ Re-evaluar cuando Today emita "subida hecha"
    if ((this.challengeService as any).submissionMade$) {
      this.subs.push(
        (this.challengeService as any).submissionMade$.subscribe(() =>
          this.refreshExploreLock()
        )
      );
    }

    // Evalúa el estado del tab de Explore al iniciar
    this.refreshExploreLock();
  }

  ngOnDestroy(): void {
    this.navSub?.unsubscribe();
    this.subs.forEach((s) => s.unsubscribe());
    this.applyTabbarVisibility(false);
  }

  // ——— helpers ———
  private applyTabbarVisibility(hide: boolean) {
    const el = document.querySelector('ion-tab-bar') as HTMLElement | null;
    if (el) el.style.display = hide ? 'none' : '';
  }

  /**
   * 🔍 Refresca:
   *  - lastCompletedInterval (leyendo userProgress/<uid>.lastCompletedInterval)
   *  - si Explore debe estar bloqueado (no hay ningún reto hecho)
   */
  private async refreshExploreLock() {
    if (this.refreshing) return;
    this.refreshing = true;

    try {
      const user = this.auth.currentUser;
      if (!user) {
        this.exploreDisabled.set(true);
        this.lastCompletedInterval = null;
        return;
      }

      const ref = doc(this.firestore, `userProgress/${user.uid}`);
      const snap = await runInInjectionContext(this.injector, () => getDoc(ref));

      if (!snap.exists()) {
        this.exploreDisabled.set(true);
        this.lastCompletedInterval = null;
        return;
      }

      const data: any = snap.data() || {};
      const last =
        typeof data.lastCompletedInterval === 'number' &&
        data.lastCompletedInterval > 0
          ? data.lastCompletedInterval
          : null;

      this.lastCompletedInterval = last;
      this.exploreDisabled.set(!last);
    } catch (e) {
      console.error('Error leyendo lastCompletedInterval para Explore:', e);
      this.exploreDisabled.set(true);
      this.lastCompletedInterval = null;
    } finally {
      this.refreshing = false;
    }
  }

  /**
   * 🧭 Click en Explore:
   *  - Siempre navegamos a /tabs/explore
   *  - La pantalla decide qué mostrar
   */
  async onExploreClick(ev: Event) {
    ev.preventDefault();

    // 🧹 Limpia cualquier “pegado” de feeds abiertos desde Profile
    try {
      sessionStorage.removeItem('explore_sticky_day');
      sessionStorage.removeItem('explore_sticky_active');
    } catch {}

    this.router.navigate(['/tabs/explore'], {
      queryParams: {},
      queryParamsHandling: '',
    });
  }
}
